import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';
import {
  DialogShellComponent,
  FilledButtonComponent,
  LinkButtonComponent,
  OutlinedButtonComponent,
  TextFieldComponent,
} from '@custom-components';
import { PartnershipTermsDialogComponent } from '../partnership-terms-dialog/partnership-terms-dialog.component';

export type PartnershipAction = 'accept' | 'decline' | 'extend' | 'revoke';

export interface PartnershipActionDialogData {
  action: PartnershipAction;
  ownerPartnerName: string;
  guestPartnerName: string;
}

/** Result for an `extend` confirmation; other actions just close with `true`. */
export interface PartnershipExtendResult {
  reviewDueDate: string;
}

/** Today + 1 year as a local `YYYY-MM-DD` string for the extend date default. */
function oneYearFromToday(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

@Component({
  selector: 'xemex-partnership-action-dialog',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatCheckboxModule,
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    OutlinedButtonComponent,
    TextFieldComponent,
    LinkButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './partnership-action-dialog.component.html',
})
export class PartnershipActionDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<PartnershipActionDialogComponent>);
  private readonly dialog = inject(MatDialog);
  readonly data = inject<PartnershipActionDialogData>(MAT_DIALOG_DATA);

  readonly isExtend = this.data.action === 'extend';
  /** Accepting requires the guest to explicitly agree to the terms and conditions. */
  readonly isAccept = this.data.action === 'accept';
  /** Destructive actions get a red confirm button. */
  readonly isDestructive = this.data.action === 'decline' || this.data.action === 'revoke';

  readonly titleKey = `partnerships.actions.${this.data.action}.title`;
  readonly messageKey = `partnerships.actions.${this.data.action}.message`;
  readonly confirmKey = `partnerships.actions.${this.data.action}.confirm`;

  readonly reviewDueDate = new FormControl(oneYearFromToday(), {
    nonNullable: true,
    validators: Validators.required,
  });

  /** Whether the guest ticked the terms-and-conditions agreement (accept only). */
  readonly termsAgreed = signal(false);
  /** Set once the user tries to confirm without agreeing, to surface the validation message. */
  readonly termsTouched = signal(false);

  onToggleTerms(checked: boolean): void {
    this.termsAgreed.set(checked);
    if (checked) {
      this.termsTouched.set(false);
    }
  }

  /** Opens the read-only terms and conditions dialog. */
  onViewTerms(): void {
    this.dialog.open(PartnershipTermsDialogComponent, {
      width: '42rem',
      maxWidth: 'calc(100vw - 2rem)',
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (this.isAccept) {
      if (!this.termsAgreed()) {
        this.termsTouched.set(true);
        return;
      }
      this.dialogRef.close(true);
      return;
    }
    if (!this.isExtend) {
      this.dialogRef.close(true);
      return;
    }
    if (this.reviewDueDate.invalid) {
      this.reviewDueDate.markAsTouched();
      return;
    }
    this.dialogRef.close({
      reviewDueDate: this.reviewDueDate.value,
    } satisfies PartnershipExtendResult);
  }
}
